/**
 * SocialSwipe - Main Application Entry Point
 * A modular PWA for social event matching
 */

console.log('socialswipe.js is loading...');

// ============================================================================
// INSTALL PROMPT - Must capture before DOMContentLoaded
// ============================================================================
let deferredInstallPrompt = null;

window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    console.log('[Install] beforeinstallprompt captured');
    // If app is already initialized, show the banner
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
        setupInstallPrompt();
    }
});

window.addEventListener('appinstalled', () => {
    console.log('[Install] App installed');
    deferredInstallPrompt = null;
    hideInstallBanner();
    // Update profile install status if visible
    const statusEl = document.getElementById('install-status-text');
    if (statusEl) {
        import('./js/utils/i18n.js').then(({ t }) => {
            statusEl.textContent = t('profile.installedStandalone');
            statusEl.className = 'text-sm text-green-600';
        });
    }
});

// Import all modules
import { initializeState, VERSION } from './js/config.js';
import { initAuth } from './js/modules/auth.js';
import { setupHomeEventListeners, loadHomeData } from './js/modules/home.js';
import { setupEventDetailsEventListeners } from './js/modules/eventDetails.js';
// QR scanner loaded lazily on demand
let _qrModule = null;
async function getQRModule() {
    if (!_qrModule) _qrModule = await import('./js/modules/qrscanner.js');
    return _qrModule;
}
import { setupSwipingEventListeners, loadSwipeUsers } from './js/modules/swiping.js';
import { setupMatchesEventListeners, loadMatches } from './js/modules/matches.js';
import { setupProfileEventListeners, loadProfile } from './js/modules/profile.js';
import { startPolling } from './js/modules/polling.js';
// Mixer loaded lazily on demand
let _mixerModule = null;
async function getMixerModule() {
    if (!_mixerModule) _mixerModule = await import('./js/modules/mixer.js');
    return _mixerModule;
}
import { setupNotificationListeners, fetchUnreadCount, handlePushNotificationClick } from './js/modules/notifications.js';
import { Navigation, Utils } from './js/utils/ui.js';
import { AppState, updateState } from './js/config.js';
import { initFingerprint } from './js/services/device-id.js';
import { installGlobalErrorHandlers } from './js/services/errorReporter.js';
import { initI18n, applyTranslations, getLang, t } from './js/utils/i18n.js';
// Legal module loaded lazily (terms & privacy modals)
import { initHistory, pushScreen, pushModalState, removeModalState, installPopstateListener } from './js/utils/history.js';

/**
 * Initialize the application when DOM is ready
 */
document.addEventListener('DOMContentLoaded', function() {
    console.log('DOM loaded, initializing app...');
    initializeApp();
});

/**
 * Main application initialization
 */
function initializeApp() {
    // Install global error handlers — catches unhandled errors and reports them to backend
    installGlobalErrorHandlers();

    // Pre-load browser fingerprint in background (so it's ready by the time auth needs it)
    initFingerprint();

    // Capture /join/:code from pathname BEFORE initHistory replaces the URL
    const joinMatch = window.location.pathname.match(/^\/join\/([^/]+)/);
    if (joinMatch) {
        localStorage.setItem('pendingInvite', joinMatch[1]);
        window.history.replaceState({}, document.title, '/' + window.location.search);
    }

    // Capture OAuth token from URL BEFORE initHistory strips query params
    const urlParams = new URLSearchParams(window.location.search);
    const oauthToken = urlParams.get('token');
    const oauthError = urlParams.get('error');

    // Initialize application state
    initializeState();

    // Initialize i18n (sets lang/dir on <html>, applies translations to static DOM)
    initI18n();
    updateLangSwitcherUI();

    // Read saved page from URL hash before initHistory overwrites it
    const hash = window.location.hash.replace('#/', '');
    const restorablePages = ['home', 'matches', 'profile'];
    const savedPage = restorablePages.includes(hash) ? hash : 'home';

    // Initialize history (replaceState for the initial screen)
    initHistory(savedPage);

    // Install back-button (popstate) handler
    installPopstateListener({
        closeModal: (modalId) => {
            // Special handling for notification center (slide-out animation)
            if (modalId === 'notification-center') {
                const panel = document.getElementById('notification-center-panel');
                if (panel) {
                    const isRtl = document.documentElement.dir === 'rtl';
                    panel.style.transform = isRtl ? 'translateX(-100%)' : 'translateX(100%)';
                }
                const el = document.getElementById(modalId);
                if (el) setTimeout(() => el.classList.add('hidden'), 300);
                return;
            }
            if (modalId === 'notification-detail-modal') {
                const el = document.getElementById(modalId);
                if (el) el.classList.add('hidden');
                return;
            }
            const el = document.getElementById(modalId);
            if (el) {
                el.classList.add('hidden');
                document.body.style.overflow = 'auto';
            }
        },
        navigateTo: (screen) => navigateToPage(screen),
        currentScreen: () => AppState.currentPage || 'home',
    });

    // Setup all event listeners
    setupAllEventListeners();

    // Listen for language changes to re-render dynamic content & update switcher UI
    document.addEventListener('langchange', () => {
        updateLangSwitcherUI();
        // Re-render current page dynamic content
        const page = AppState.currentPage || 'home';
        switch (page) {
            case 'home': loadHomeData(); break;
            case 'matches': loadMatches(); break;
            case 'profile': loadProfile(); break;
            case 'swiping':
                if (AppState.currentEvent) loadSwipeUsers(AppState.currentEvent.id);
                break;
        }
        // Reposition nav indicator after direction change (LTR ↔ RTL)
        requestAnimationFrame(() => Navigation.refreshIndicator(page));
    });

    // Keep nav indicator aligned on window resize
    window.addEventListener('resize', () => {
        Navigation.refreshIndicator(AppState.currentPage || 'home');
    });

    // Initialize authentication (this will handle login state and routing)
    initAuth(savedPage, { oauthToken, oauthError });

    // Start polling for updates
    startPolling();

    // Setup install prompt after auth
    setupInstallPrompt();
    setupInstallRetrigger();
}

/**
 * Update language switcher UI to highlight active language
 */
function updateLangSwitcherUI() {
    const lang = getLang();
    document.querySelectorAll('.lang-option').forEach(el => {
        el.classList.toggle('active', el.getAttribute('data-lang') === lang);
    });
}

/**
 * Setup all event listeners for the application
 */
function setupAllEventListeners() {
    // Navigation listeners
    setupNavigationListeners();

    // Module-specific listeners
    setupHomeEventListeners();
    setupEventDetailsEventListeners();
    getQRModule().then(m => m.setupQRScannerEventListeners());
    setupSwipingEventListeners();
    setupMatchesEventListeners();
    setupProfileEventListeners();
    getMixerModule().then(m => m.setupMixerEventListeners());
    setupNotificationListeners();
    import('./js/utils/legal.js');
}

/**
 * Setup navigation event listeners
 */
function setupNavigationListeners() {
    // Main navigation buttons
    document.querySelectorAll('.nav-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const page = e.currentTarget.dataset.page;
            navigateToPage(page);
        });
    });
}

/**
 * Navigate to a specific page and load its data
 */
function navigateToPage(page) {
    // Update navigation UI
    const currentPage = Navigation.navigateToPage(page);
    updateState({ currentPage });

    // Push history entry for back-button support
    pushScreen(page);

    // Load page-specific data
    switch (page) {
        case 'home':
            loadHomeData();
            break;
        case 'event-details':
            // Data already populated by showEventDetails() before navigation
            break;
        case 'matches':
            loadMatches();
            break;
        case 'profile':
            loadProfile();
            break;
        case 'swiping':
            if (AppState.currentEvent) {
                loadSwipeUsers(AppState.currentEvent.id);
            } else {
                showSwipingEmptyState();
            }
            break;
    }
}

/**
 * Show a friendly empty state on the swiping page when no event is selected
 */
function showSwipingEmptyState() {
    const container = document.getElementById('swipe-container');
    if (container) {
        container.innerHTML = `
            <div class="text-center py-12">
                <div class="text-5xl mb-4 pop-in">✨</div>
                <h3 class="text-lg font-medium text-foreground mb-2">${Utils.escapeHtml(t('swiping.noEventSelected'))}</h3>
                <p class="text-muted-foreground mb-6">${Utils.escapeHtml(t('swiping.joinEventToSwipe'))}</p>
                <button onclick="navigateToPage('home')" class="shadcn-button shadcn-button-primary btn-press breathe-glow">
                    <i class="fas fa-calendar-alt mr-2"></i>${Utils.escapeHtml(t('swiping.browseEvents'))}
                </button>
            </div>
        `;
    }
    // Clear event info subtitle
    const eventInfo = document.getElementById('event-info');
    if (eventInfo) eventInfo.textContent = t('swiping.subtitle');
}

// Make navigation function globally available for HTML onclick handlers
window.navigateToPage = navigateToPage;

// Export main functions for global access if needed
window.initializeApp = initializeApp;

// ============================================================================
// KILL SWITCH - Service Worker Management
// ============================================================================

/**
 * Kill the service worker, clear all caches, clear localStorage and reload
 */
async function killServiceWorker() {
    console.log('[KillSwitch] Starting...');
    const results = { sw: false, caches: false, localStorage: false, sessionStorage: false, indexedDB: false };

    try {
        // 1. Unregister all service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const reg of registrations) {
                await reg.unregister();
                console.log('[KillSwitch] SW unregistered:', reg.scope);
            }
            results.sw = true;
        }

        // 2. Clear all caches
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('[KillSwitch] Caches cleared:', cacheNames);
            results.caches = true;
        }

        // 3. Clear localStorage
        localStorage.clear();
        results.localStorage = true;
        console.log('[KillSwitch] localStorage cleared');

        // 4. Clear sessionStorage
        sessionStorage.clear();
        results.sessionStorage = true;
        console.log('[KillSwitch] sessionStorage cleared');

        // 5. Clear all IndexedDB databases
        if (indexedDB.databases) {
            try {
                const dbs = await indexedDB.databases();
                await Promise.all(dbs.map(db => new Promise((resolve, reject) => {
                    const req = indexedDB.deleteDatabase(db.name);
                    req.onsuccess = resolve;
                    req.onerror = reject;
                })));
                results.indexedDB = true;
                console.log('[KillSwitch] IndexedDB cleared:', dbs.map(d => d.name));
            } catch (e) {
                console.warn('[KillSwitch] IndexedDB cleanup failed:', e);
            }
        }

        console.log('[KillSwitch] Complete. Reloading...');
        window.location.href = '/?t=' + Date.now();
    } catch (error) {
        console.error('[KillSwitch] Error:', error);
    }

    return results;
}

/**
 * Get current service worker status and cache info
 */
async function getServiceWorkerStatus() {
    const status = {
        supported: 'serviceWorker' in navigator,
        controller: null,
        registrations: [],
        caches: [],
        swVersion: null
    };

    try {
        if (status.supported) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            status.registrations = registrations.map(reg => ({
                scope: reg.scope,
                active: reg.active ? { state: reg.active.state, scriptURL: reg.active.scriptURL } : null,
                waiting: reg.waiting ? { state: reg.waiting.state } : null,
                installing: reg.installing ? { state: reg.installing.state } : null
            }));

            if (navigator.serviceWorker.controller) {
                status.controller = {
                    state: navigator.serviceWorker.controller.state,
                    scriptURL: navigator.serviceWorker.controller.scriptURL
                };
            }

            // Ask SW for its version
            if (navigator.serviceWorker.controller) {
                try {
                    const { port1, port2 } = new MessageChannel();
                    const versionPromise = new Promise((resolve) => {
                        port1.onmessage = (e) => resolve(e.data);
                        setTimeout(() => resolve(null), 1000);
                    });
                    navigator.serviceWorker.controller.postMessage({ type: 'GET_VERSION' }, [port2]);
                    const result = await versionPromise;
                    status.swVersion = result?.version || null;
                } catch (e) {
                    // ignore
                }
            }
        }

        if ('caches' in window) {
            const cacheNames = await caches.keys();
            status.caches = cacheNames;
        }
    } catch (error) {
        status.error = error.message;
    }

    console.log('[SW Status]', status);
    return status;
}

window.killServiceWorker = killServiceWorker;
window.getServiceWorkerStatus = getServiceWorkerStatus;

// ============================================================================
// INSTALL PROMPT HANDLING
// ============================================================================

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;
}

function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function canShowInstallBanner() {
    if (isStandalone()) return false;

    const dismissed = localStorage.getItem('install_banner_dismissed');
    if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        const dismissCount = parseInt(localStorage.getItem('install_banner_dismiss_count') || '0', 10);
        // Escalating cooldown: 1st dismiss = 2 days, 2nd = 5 days, 3rd+ = 14 days
        const cooldownDays = dismissCount <= 1 ? 2 : dismissCount === 2 ? 5 : 14;
        const cooldown = cooldownDays * 24 * 60 * 60 * 1000;
        if (Date.now() - dismissedAt < cooldown) return false;
    }

    return deferredInstallPrompt || isIOS();
}

function setupInstallPrompt() {
    if (!canShowInstallBanner()) return;

    // Show with a short delay so it doesn't compete with initial page load
    setTimeout(() => {
        if (canShowInstallBanner()) showInstallBanner();
    }, 2500);
}

// Re-show install banner on high-engagement moments (e.g. new match)
function setupInstallRetrigger() {
    if (isStandalone()) return;
    document.addEventListener('socket:new_match', () => {
        // Wait a moment so the match celebration shows first
        setTimeout(() => {
            if (canShowInstallBanner()) showInstallBanner();
        }, 4000);
    });
}

function showInstallBanner() {
    const banner = document.getElementById('install-banner');
    if (banner) {
        banner.classList.remove('hidden');
        banner.classList.add('install-slide-up');
    }
}

function hideInstallBanner() {
    const banner = document.getElementById('install-banner');
    if (banner) {
        banner.classList.add('hidden');
        banner.classList.remove('install-slide-up');
    }
}

async function triggerInstallPrompt() {
    if (isIOS()) {
        showIOSInstallModal();
        return;
    }

    if (!deferredInstallPrompt) return;

    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    console.log('[Install] User choice:', outcome);
    deferredInstallPrompt = null;
    hideInstallBanner();
}

function dismissInstallBanner() {
    const count = parseInt(localStorage.getItem('install_banner_dismiss_count') || '0', 10);
    localStorage.setItem('install_banner_dismiss_count', String(count + 1));
    localStorage.setItem('install_banner_dismissed', String(Date.now()));
    hideInstallBanner();
}

function showIOSInstallModal() {
    const modal = document.getElementById('ios-install-modal');
    if (modal) {
        modal.classList.remove('hidden');
        pushModalState('ios-install-modal');
    }
}

function closeIOSInstallModal() {
    const modal = document.getElementById('ios-install-modal');
    if (modal) {
        modal.classList.add('hidden');
        removeModalState('ios-install-modal');
    }
}

window.triggerInstallPrompt = triggerInstallPrompt;
window.dismissInstallBanner = dismissInstallBanner;
window.closeIOSInstallModal = closeIOSInstallModal;
